import { formatIntegerNumber, plainAverage, withUnit } from '@utils/formatters';
import { ReactNode } from 'react';
import { compose } from 'redux';

import { PERCENTAGE } from '../constants/units';
import createColumn from './createColumn';

export const percentageSignal = {
    unit: PERCENTAGE,
    displayedUnit: PERCENTAGE,
    format: compose<ReactNode>(withUnit(PERCENTAGE), formatIntegerNumber),
    average: plainAverage,
};

export const averageSlopeDown = createColumn({
    key: 'averageSlopeDown',
    dataField: 'averageSlopeDown',
    labelId: 'averageSlopeDown',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
    dataSort: true,
});
export const averageSlopeUp = createColumn({
    key: 'averageSlopeUp',
    dataField: 'averageSlopeUp',
    labelId: 'averageSlopeUp',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
});

export const foresightedDrivingPercentage = createColumn({
    key: 'foresightedDrivingPercentage',
    dataField: 'foresightedDrivingPercentage',
    labelId: 'foresightedDrivingPercentage',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
});

export const foresightedDrivingPercentageV2 = createColumn({
    key: 'foresightedDrivingPercentageV2',
    dataField: 'foresightedDrivingPercentageV2',
    labelId: 'details.rating',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
});

export const coastingRating = createColumn({
    key: 'coastingRating',
    dataField: 'coastingRating',
    labelId: 'discipline.coasting',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
    icon: 'rioglyph rioglyph-coasting',
    hideLabelOnTable: true,
});

export const accelerationPedalRating = createColumn({
    key: 'accelerationPedalRating',
    dataField: 'accelerationPedalRating',
    labelId: 'discipline.accelerationPedal',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
    icon: 'rioglyph rioglyph-kickdown',
    hideLabelOnTable: true,
});

export const accelerationPedalRatingV2 = createColumn({
    key: 'accelerationPedalRatingV2',
    dataField: 'accelerationPedalRatingV2',
    labelId: 'discipline.accelerationPedal',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
    icon: 'rioglyph rioglyph-kickdown',
    hideLabelOnTable: true,
});

export const brakingRating = createColumn({
    key: 'brakingRating',
    dataField: 'brakingRating',
    labelId: 'discipline.braking',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
    icon: 'rioglyph rioglyph-brake',
    hideLabelOnTable: true,
});

export const cruiseControlRating = createColumn({
    key: 'cruiseControlRating',
    dataField: 'cruiseControlRating',
    labelId: 'discipline.cruiseControl',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
    icon: 'rioglyph rioglyph-cruise-control',
    hideLabelOnTable: true,
});

export const cruiseControlRatingV2 = createColumn({
    key: 'cruiseControlRatingV2',
    dataField: 'cruiseControlRatingV2',
    labelId: 'discipline.cruiseControl',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
    icon: 'rioglyph rioglyph-cruise-control',
    hideLabelOnTable: true,
});

export const cruiseControlRatingV3 = createColumn({
    key: 'cruiseControlRatingV3',
    dataField: 'cruiseControlRatingV3',
    labelId: 'discipline.cruiseControl',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
    icon: 'rioglyph rioglyph-cruise-control',
    hideLabelOnTable: true,
});

export const overspeedRating = createColumn({
    key: 'overspeedRating',
    dataField: 'overspeedRating',
    labelId: 'discipline.overspeed',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
    icon: 'rioglyph rioglyph-speed',
    hideLabelOnTable: true,
});

export const totalRating = createColumn({
    key: 'totalRating',
    dataField: 'foresightedDrivingPercentage',
    labelId: 'details.rating',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
});

export const totalRatingWithoutCC = createColumn({
    key: 'totalRatingWithoutCC',
    dataField: 'foresightedDrivingPercentageWithoutCc',
    labelId: 'details.rating',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
});

export const totalRatingV2 = createColumn({
    key: 'totalRatingV2',
    dataField: 'foresightedDrivingPercentageV2',
    labelId: 'details.rating',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
});

export const fuelForesightedDrivingPercentage = createColumn({
    key: 'fuelForesightedDrivingPercentage',
    dataField: 'foresightedDrivingPercentageV2',
    labelId: 'rating.fuel',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
});

export const totalRatingV3 = createColumn({
    key: 'totalRatingV3',
    dataField: 'foresightedDrivingPercentageV3',
    labelId: 'details.rating',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
});

export const totalRatingWithoutCCV2 = createColumn({
    key: 'totalRatingWithoutCCV2',
    dataField: 'foresightedDrivingPercentageWithoutCcV2',
    labelId: 'details.rating',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
});

export const harshAccelerationRating = createColumn({
    key: 'harshAccelerationRating',
    dataField: 'harshAccelerationRating',
    labelId: 'discipline.harshAcceleration',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
    icon: 'rioglyph rioglyph-exclamation-sign',
});

export const harshBrakingRating = createColumn({
    key: 'harshBrakingRating',
    dataField: 'harshBrakingRating',
    labelId: 'discipline.harshBraking',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
    icon: 'rioglyph rioglyph-dangerousgoods',
});

export const excessiveIdlingRating = createColumn({
    key: 'excessiveIdlingRating',
    dataField: 'excessiveIdlingRating',
    labelId: 'discipline.excessiveIdling',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
    icon: 'rioglyph rioglyph-hour-glass',
    hideLabelOnTable: true,
});

export const electricRecuperationRating = createColumn({
    key: 'electricRecuperationRating',
    dataField: 'electricRecuperationRating',
    labelId: 'electricRecuperation',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
    icon: 'rioglyph rioglyph-refresh',
    hideLabelOnTable: true,
});

export const electricAccelerationPedalRating = createColumn({
    key: 'electricAccelerationPedalRating',
    dataField: 'electricAccelerationPedalRating',
    labelId: 'discipline.accelerationPedal',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
    icon: 'rioglyph rioglyph-kickdown',
    hideLabelOnTable: true,
});

export const electricOverspeedRating = createColumn({
    key: 'electricOverspeedRating',
    dataField: 'electricOverspeedRating',
    labelId: 'discipline.overspeed',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
    icon: 'rioglyph rioglyph-speed',
    hideLabelOnTable: true,
});

export const electricCruiseControlRating = createColumn({
    key: 'electricCruiseControlRating',
    dataField: 'electricCruiseControlRating',
    labelId: 'discipline.cruiseControl',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
    icon: 'rioglyph rioglyph-cruise-control',
    hideLabelOnTable: true,
});

export const electricForesightedDrivingPercentage = createColumn({
    key: 'electricForesightedDrivingPercentage',
    dataField: 'electricForesightedDrivingPercentage',
    labelId: 'rating.electric',
    formatter: percentageSignal.format,
    unit: percentageSignal.unit,
});
